'use client';

import { useEffect, useRef, useCallback, useState } from 'react';

import { useAui, useAuiEvent, useAuiState } from '@assistant-ui/react';
import { useLatest } from 'react-use';

import { useConfigValue } from '@/components/providers/config-provider';
import { useReturnThread } from '@/hooks/use-return-thread';
import { useSafePostHog } from '@/hooks/use-safe-posthog';
import { useAssistantCloudStore } from '@/lib/assistant-cloud-local/stores/assistant-cloud-store';
import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { hasKey } from '@/lib/history-key/cipher';
import { useHistorySession } from '@/lib/history-key/session';
import {
  isSwitchBroadcastSuppressed,
  setResumableActiveThread,
} from '@/lib/hitl/resumable-storage';
import { useSafeSessionStorage } from '@/lib/storage/use-safe-session-storage';
import { SYNC_EVENTS } from '@/lib/sync/events';
import { useCrossTabSync } from '@/lib/sync/hooks/use-cross-tab-sync';
import { useSyncEvent } from '@/lib/sync/hooks/use-sync-event';
import { clearNewConversationRequest, readBootThreadId, useSyncStore } from '@/lib/sync/store';
import {
  isThreadReady,
  findThread,
  getForceThreadRestore,
  clearForceThreadRestore,
  isLocalThreadId,
  getOrCreateThreadUuid,
  setPendingLocalThreadId,
} from '@/lib/utils/thread';

const PENDING_THREAD_KEY = 'pending-thread-switch';

export function useThreadPersistence(): void {
  const api = useAui();
  const { emit } = useCrossTabSync();
  const posthog = useSafePostHog();
  // In server-persistence mode the IDB store never initializes (it's never constructed);
  // thread restore is gated on the server-backed thread list instead.
  const isServerPersistence = useConfigValue<'local' | 'server'>('persistence') === 'server';
  const isIdbStoreInitialized = useAssistantCloudStore(state => state.isInitialized);
  const isStoreInitialized = isServerPersistence || isIdbStoreInitialized;
  const threadItems = useAuiState(state => state.threads.threadItems);
  const mainThreadId = useAuiState(state => state.threads.mainThreadId);

  const setPersistedThreadId = useSyncStore(state => state.setThreadId);
  const setPersistedThreadIdRef = useLatest(setPersistedThreadId);

  const [pendingThreadId, setPendingThreadId] = useSafeSessionStorage<string | null>(
    PENDING_THREAD_KEY,
    null,
  );

  const isHandlingRemoteSwitchRef = useRef(false);
  const currentThreadIdRef = useRef<string | null>(null);
  const newThreadLocalIdsRef = useRef<Set<string>>(new Set());

  const apiRef = useLatest(api);
  const threadItemsRef = useLatest(threadItems);

  const handleRunCompletion = useCallback(
    (threadId: string) => {
      const thread = findThread(threadItemsRef.current, threadId);
      const remoteId = thread?.remoteId;
      const wasNewThread = newThreadLocalIdsRef.current.has(threadId);

      if (wasNewThread && remoteId) {
        newThreadLocalIdsRef.current.delete(threadId);
        setPersistedThreadId(remoteId);
        emit(SYNC_EVENTS.THREAD_SWITCH, remoteId);
        emit(SYNC_EVENTS.RUNTIME_FINISH, { threadId: remoteId });
      } else {
        emit(SYNC_EVENTS.RUNTIME_FINISH, null);
      }
    },
    [threadItemsRef, setPersistedThreadId, emit],
  );

  const switchToThreadSafely = useCallback(
    function switchToThreadSafely({ threadId, draft }: { threadId: string; draft?: string }) {
      try {
        const thread = findThread(threadItemsRef.current, threadId);

        const stored =
          !isServerPersistence &&
          thread &&
          useAssistantCloudStore.getState().threads.get(thread.remoteId ?? thread.id);
        if (
          !thread ||
          (!isServerPersistence &&
            thread.status !== 'new' &&
            (!stored || (stored.owner && !hasKey(stored.owner))))
        ) {
          isHandlingRemoteSwitchRef.current = false;
          setPendingThreadId(null);
          return false;
        }

        if (thread.id === currentThreadIdRef.current) {
          isHandlingRemoteSwitchRef.current = false;
          setPendingThreadId(null);
          return true;
        }

        isHandlingRemoteSwitchRef.current = true;
        // Typed void, but returns a promise that settles once the thread's runtime is attached.
        const switched = Promise.resolve(apiRef.current.threads.switchToThread(thread.id));
        if (draft) {
          void switched
            .then(function restoreDraft() {
              if (apiRef.current.threads.getState().mainThreadId === thread.id) {
                apiRef.current.composer.setText(draft);
              }
            })
            .catch(function reportFailedSwitch(error: unknown) {
              posthog?.captureException(error);
            });
        }
        setPendingThreadId(null);
        return true;
      } catch (error) {
        posthog?.captureException(error);
        isHandlingRemoteSwitchRef.current = false;
        setPendingThreadId(null);
        return false;
      }
    },
    [apiRef, threadItemsRef, setPendingThreadId, posthog, isServerPersistence],
  );

  // Restore-after-reload is handled by the FRAMEWORK now: the runtime provider passes the
  // persisted id (readBootThreadId) as `initialThreadId` to useChatRuntime, and aui's
  // __internal_load switches to it — adopting unlisted ids through the adapter, swallowing
  // failures. This hook only keeps the bookkeeping around that restore: same boot id, read once.
  const [bootThreadId] = useState(() => readBootThreadId());
  // One-shot: consumed by the mainThreadId effect to tell the framework's restore switch apart
  // from a user-initiated switch (no cross-tab emit / analytics for the former).
  const bootRestoreRemoteIdRef = useRef<string | null>(bootThreadId);

  useEffect(() => {
    // The force-restore flag is a one-shot boot signal (readBootThreadId and the sync-store
    // merge both honored it above) — clear it so it doesn't leak into future boots.
    if (getForceThreadRestore()) {
      clearForceThreadRestore();
    }
    clearNewConversationRequest();
  }, []);

  // Replacement for the old adopt-failure cleanup: if the boot id never materializes as a
  // thread item (deleted thread / stale ghost id — the framework's initialThreadId switch
  // failed silently), drop the pointer, or every future boot repeats the 404 adoption attempt.
  useEffect(() => {
    if (!bootThreadId || !isStoreInitialized) return;
    const timer = setTimeout(() => {
      const stillPersisted = useSyncStore.getState().threadId === bootThreadId;
      // Only clear once the thread list has actually loaded (has entries): clearing while the list
      // is still hydrating would race slow adoption and drop the restore hint for a thread that
      // does exist. An empty list means "not loaded yet", so leave the pointer and let a real
      // switch reconcile — the cost is at most one swallowed 404 discovery on the next boot.
      const listLoaded = threadItemsRef.current.length > 0;
      if (stillPersisted && listLoaded && !findThread(threadItemsRef.current, bootThreadId)) {
        setPersistedThreadIdRef.current(null);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [bootThreadId, isStoreInitialized, threadItemsRef, setPersistedThreadIdRef]);

  useAuiEvent('thread.initialize', ({ threadId }: { threadId: string }) => {
    if (isLocalThreadId(threadId)) {
      newThreadLocalIdsRef.current.add(threadId);
    }
  });

  // mainThreadId changes, not threadListItem.switchedTo which doesn't fire at root scope.
  const prevMainThreadIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!mainThreadId || mainThreadId === prevMainThreadIdRef.current) return;

    const isInitialMount = prevMainThreadIdRef.current === null;
    const fromThreadLocalId = prevMainThreadIdRef.current;
    prevMainThreadIdRef.current = mainThreadId;

    if (isInitialMount) {
      currentThreadIdRef.current = mainThreadId;
      setResumableActiveThread(findThread(threadItemsRef.current, mainThreadId)?.remoteId ?? null);
      return;
    }

    const thread = findThread(threadItemsRef.current, mainThreadId);
    const remoteId = thread?.remoteId ?? null;
    const isRemoteSwitch = isHandlingRemoteSwitchRef.current;
    // The framework's initialThreadId restore lands as a plain mainThreadId change; it's not a
    // user switch, so it must not emit cross-tab or count as a switch_to_thread.
    const isBootRestore = remoteId !== null && remoteId === bootRestoreRemoteIdRef.current;
    // Landed, or the guest went elsewhere first: either way no later unlock may restore it.
    bootRestoreRemoteIdRef.current = null;

    currentThreadIdRef.current = mainThreadId;
    isHandlingRemoteSwitchRef.current = false;
    setResumableActiveThread(remoteId);
    setPersistedThreadId(remoteId);

    // Only the tab where the user actually switched emits, so the analytics event
    // isn't double-counted. Report remoteId when resolved to match the rest of the
    // analytics.
    // isSwitchBroadcastSuppressed: this tab is adopting a sibling's stream (following, not leading),
    // so its own bounce must not tell other tabs to follow it (esp. to the intermediate blank).
    if (!isRemoteSwitch && !isBootRestore && !isSwitchBroadcastSuppressed()) {
      emit(SYNC_EVENTS.THREAD_SWITCH, remoteId);
      const fromThread = fromThreadLocalId
        ? findThread(threadItemsRef.current, fromThreadLocalId)
        : null;
      posthog?.capture(CHAT_ANALYTICS_EVENTS.SWITCH_TO_THREAD, {
        navigatedFromThreadId: fromThread?.remoteId ?? fromThreadLocalId,
        navigatedToThreadId: remoteId ?? mainThreadId,
      });
    }
  }, [mainThreadId, threadItemsRef, setPersistedThreadId, emit, posthog]);

  useAuiEvent('thread.runEnd', ({ threadId }: { threadId: string }) => {
    handleRunCompletion(threadId);
  });

  useSyncEvent(SYNC_EVENTS.THREAD_SWITCH, (threadId: string | null) => {
    if (isLocalThreadId(threadId)) {
      return;
    }

    isHandlingRemoteSwitchRef.current = true;

    if (threadId === null) {
      setPendingThreadId(null);
      apiRef.current.threads.switchToNewThread();
    } else if (isThreadReady(threadItemsRef.current, threadId)) {
      switchToThreadSafely({ threadId });
    } else {
      setPendingThreadId(threadId);
    }
  });

  // Count, not threadItems, to avoid re-running during streaming.
  const threadItemsCount = threadItems.length;

  useEffect(() => {
    if (!pendingThreadId || isLocalThreadId(pendingThreadId)) return;

    if (isThreadReady(threadItemsRef.current, pendingThreadId)) {
      switchToThreadSafely({ threadId: pendingThreadId });
    }
  }, [pendingThreadId, threadItemsCount, threadItemsRef, switchToThreadSafely]);

  // An account's conversation stays locked until the session lookup brings its key, which usually
  // lands after the framework's boot restore gave up on it. Once the history reopens with that key,
  // the boot thread opens after all, unless the guest has started on the blank thread meanwhile.
  const historyOwner = useHistorySession(state => state.owner);
  useEffect(
    function restoreBootThreadOnceUnlocked() {
      const bootRemoteId = bootRestoreRemoteIdRef.current;
      if (!bootRemoteId || !historyOwner || !mainThreadId) return;
      if (!isThreadReady(threadItemsRef.current, bootRemoteId)) return;
      const blank =
        findThread(threadItemsRef.current, mainThreadId)?.status === 'new' &&
        !apiRef.current.composer.getState().text;
      if (!blank) {
        bootRestoreRemoteIdRef.current = null;
        return;
      }
      switchToThreadSafely({ threadId: bootRemoteId });
    },
    [historyOwner, mainThreadId, threadItemsCount, threadItemsRef, apiRef, switchToThreadSafely],
  );

  // Back to the conversation open when history locked, once the history lock has reopened it for
  // the same account, unless the guest has moved on from the blank thread it left them on.
  const returnThreadId = useReturnThread(state => state.threadId);
  useEffect(
    function returnToThreadOnceConfirmed() {
      if (!returnThreadId) return;
      useReturnThread.setState({ threadId: null });
      if (findThread(threadItemsRef.current, mainThreadId)?.status !== 'new') return;
      switchToThreadSafely({
        threadId: returnThreadId,
        draft: apiRef.current.composer.getState().text,
      });
    },
    [returnThreadId, mainThreadId, threadItemsRef, apiRef, switchToThreadSafely],
  );

  useEffect(() => {
    threadItems.forEach(thread => {
      if (isLocalThreadId(thread.id) && !thread.remoteId) {
        try {
          getOrCreateThreadUuid(thread.id);
          setPendingLocalThreadId(thread.id);
        } catch (error) {
          posthog?.captureException(error);
        }
      }
    });
  }, [threadItems, posthog]);
}
